export type SelectionState =
  | {
      selectedIds: Set<string>
      excludedIds: null
      isAllSelected: false
    }
  | {
      selectedIds: null
      excludedIds: Set<string> | null
      isAllSelected: true
    }

export function usePaginatedSelectionState(
  _totalItems: MaybeRefOrGetter<number>,
) {
  const totalItems = toRef(_totalItems)

  const selectionState = ref<SelectionState>({
    selectedIds: new Set<string>(),
    excludedIds: null,
    isAllSelected: false,
  })

  function deselectAll() {
    selectionState.value = {
      selectedIds: new Set<string>(),
      excludedIds: null,
      isAllSelected: false,
    }
  }

  function selectAll() {
    selectionState.value = {
      selectedIds: null,
      excludedIds: new Set<string>(),
      isAllSelected: true,
    }
  }

  // Assumes multiselect is true & isAllSelected is false
  function toggleSelection(id: string, preventDeselection: boolean = false) {
    if (!selectionState.value.selectedIds) {
      console.warn('Attempted to toggle selection on a null selectedIds set')
      return
    }

    if (selectionState.value.selectedIds.has(id) && !preventDeselection) {
      selectionState.value.selectedIds.delete(id)
    } else {
      selectionState.value.selectedIds.add(id)
    }

    if (selectionState.value.selectedIds.size === totalItems.value) {
      selectAll()
    }
  }

  // Assumes multiselect is false & forces isAllSelected to false
  function selectOnly(id: string) {
    if (selectionState.value.isAllSelected) {
      deselectAll()
    }

    if (!selectionState.value.selectedIds) {
      console.warn('Attempted to select only on a null selectedIds set')
      return
    }

    selectionState.value.excludedIds = null
    selectionState.value.isAllSelected = false

    selectionState.value.selectedIds.clear()
    selectionState.value.selectedIds.add(id)
  }

  // Assumes multiselect is true & isAllSelected is true
  function toggleExclusion(id: string) {
    if (!selectionState.value.excludedIds) {
      console.warn('Attempted to toggle exclusion on a null excludedIds set')
      return
    }

    if (selectionState.value.excludedIds.has(id)) {
      selectionState.value.excludedIds.delete(id)
    } else {
      selectionState.value.excludedIds.add(id)
    }

    if (selectionState.value.excludedIds.size === totalItems.value) {
      deselectAll()
    }
  }

  function toggleSelectedId(
    id: string,
    multiselect: boolean = false,
    preventDeselection: boolean = false,
  ) {
    if (selectionState.value.isAllSelected) {
      if (!multiselect) {
        selectOnly(id)
        return
      }

      toggleExclusion(id)
    } else {
      if (!multiselect) {
        selectOnly(id)
        return
      }

      toggleSelection(id, preventDeselection)
    }
  }

  // Adds every id in the given range to the current selection, never removing any
  function selectRange(ids: string[]) {
    if (selectionState.value.isAllSelected) {
      if (!selectionState.value.excludedIds) {
        console.warn('Attempted to select a range on a null excludedIds set')
        return
      }

      ids.forEach((id) => selectionState.value.excludedIds?.delete(id))
      return
    }

    if (!selectionState.value.selectedIds) {
      console.warn('Attempted to select a range on a null selectedIds set')
      return
    }

    ids.forEach((id) => selectionState.value.selectedIds?.add(id))

    if (selectionState.value.selectedIds.size === totalItems.value) {
      selectAll()
    }
  }

  const isAllSelected = computed(() => selectionState.value.isAllSelected)
  const excludedIds = computed(() => selectionState.value.excludedIds)
  const selectedIds = computed(() => selectionState.value.selectedIds)
  const selectedCount = computed(() => {
    if (selectionState.value.isAllSelected) {
      return totalItems.value - (selectionState.value.excludedIds?.size ?? 0)
    } else {
      return selectionState.value.selectedIds?.size ?? 0
    }
  })

  return {
    state: selectionState,
    isAllSelected,
    excludedIds,
    selectedIds,
    selectedCount,
    toggleSelectedId,
    selectRange,
    selectAll,
    deselectAll,
  }
}
